import { useState, useEffect, useMemo } from 'react';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

// Simplified team type matching GET_TEAMS response
export type SimpleTeam = {
  teamId: number;
  teamName: string;
  parentId: number | null;
};

/**
 * Filter out special system teams and admin teams
 * - "All Teams" - UI meta-team
 * - TeamId 9999 - System team for public notes
 * - Teams with "admin" in name (case-insensitive)
 */
const filterSpecialTeams = (teams: SimpleTeam[]): SimpleTeam[] => {
  return teams.filter(team => {
    const nameLower = team.teamName.toLowerCase();
    return nameLower !== 'all teams' &&
      team.teamId !== 9999 &&
      !nameLower.includes('admin');
  });
};

/**
 * Filter to only include leaf teams (teams with no children)
 * A team has no children if its teamId doesn't appear as a parentId in any other team
 */
const filterLeafTeams = (teams: SimpleTeam[]): SimpleTeam[] => {
  // Get set of all teamIds that are parents
  const parentTeamIds = new Set(
    teams
      .filter(team => team.parentId !== null)
      .map(team => team.parentId)
  );

  // Only include teams that are not parents (leaf nodes)
  return teams.filter(team => !parentTeamIds.has(team.teamId));
};

/**
 * Custom hook to fetch all teams from the database
 * Returns filtered list (excluding special/admin/parent teams) sorted alphabetically
 * Only includes leaf teams (teams with no children)
 */
export const useAllTeams = () => {
  const [rawTeams, setRawTeams] = useState<SimpleTeam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllTeams = async () => {
      try {
        const response = await request(API_ENDPOINTS.GET_TEAMS);
        setRawTeams(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load teams');
        console.error('Error loading all teams:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllTeams();
  }, []);

  // Filter and sort teams with useMemo for performance
  const allTeams = useMemo(() => {
    const specialFiltered = filterSpecialTeams(rawTeams);
    const leafTeams = filterLeafTeams(specialFiltered);
    return leafTeams.sort((a, b) => a.teamName.localeCompare(b.teamName));
  }, [rawTeams]);

  return { allTeams, isLoading, error };
};
